import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Megaphone,
  CalendarClock,
  Users,
  PenTool,
  Search,
  Target,
  Globe,
  BarChart3,
  Sparkles,
  GraduationCap,
  Clock,
  IndianRupee,
  CheckCircle2,
  Share2,
  Send,
  ArrowRight,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SkillstemAttribution } from "@/components/SkillstemAttribution";
import { InternApplicationForm } from "@/components/careers/InternApplicationForm";
import { toast } from "sonner";

const APPLY_URL = "#apply";
const APPLICATION_DEADLINE = "15 May 2026";
const PUBLIC_PAGE_URL = "https://we-help.in/careers/digital-marketing-intern";
const WHATSAPP_SHARE_MESSAGE = `We-Help.in is hiring Digital Marketing Interns (Part-Time, ₹5,000/month). Apply here: ${PUBLIC_PAGE_URL}`;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const trackApplyClick = (location: string) => {
  try {
    window.gtag?.("event", "apply_click", {
      event_category: "recruitment",
      event_label: "digital_marketing_intern",
      location,
    });
  } catch {
    // no-op
  }
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const scopeOfWork = [
  {
    icon: Megaphone,
    title: "Social Media Execution",
    points: ["Plan & post on Instagram, LinkedIn, X", "Engage with student community", "Track post performance"],
  },
  {
    icon: PenTool,
    title: "Content Creation",
    points: ["Reels, carousels, infographics", "Short captions & hooks", "Repurpose existing content"],
  },
  {
    icon: Search,
    title: "SEO Basics",
    points: ["Keyword research support", "On-page suggestions", "Meta titles & descriptions"],
  },
  {
    icon: Target,
    title: "Campaign Support",
    points: ["Assist with growth campaigns", "Coordinate posting schedule", "Outreach to student groups"],
  },
  {
    icon: Globe,
    title: "Website & Funnel",
    points: ["Understand user journeys", "Suggest UX improvements", "Help shape landing pages"],
  },
  {
    icon: BarChart3,
    title: "Analytics Exposure",
    points: ["Read GA / social insights", "Weekly performance notes", "Spot what's working"],
  },
];

const deliverables = [
  "12–16 social media posts per month",
  "6–8 reels/videos per month",
  "1–2 blog articles per month",
  "Basic performance tracking & reporting",
];

const benefits = [
  { icon: IndianRupee, label: "Honorarium", value: "₹5,000 / month" },
  { icon: GraduationCap, label: "Certificate", value: "From SkillSTEM Academy LLP" },
  { icon: Sparkles, label: "Real Project Experience", value: "Work on a live national platform" },
  { icon: Heart, label: "Mentorship & Guidance", value: "Learn directly from the team" },
  { icon: ArrowRight, label: "Growth Path", value: "Performance-based future opportunities" },
];

const faqs = [
  {
    q: "Is this internship paid?",
    a: "Yes. A monthly honorarium of ₹5,000 is offered along with a certificate and mentorship.",
  },
  {
    q: "Is this part-time?",
    a: "Yes. It's part-time, flexible, and output-based — not a rigid full-time office role.",
  },
  {
    q: "Who can apply?",
    a: "Students or fresh graduates interested in digital marketing, content, or social media. No prior agency experience required.",
  },
  {
    q: "Will I get a certificate?",
    a: "Yes. Successful interns receive a certificate of completion from SkillSTEM Academy LLP.",
  },
];

export default function CareersDigitalMarketingIntern() {
  const [pageUrl, setPageUrl] = useState("https://we-help.in/careers/digital-marketing-intern");

  useEffect(() => {
    document.title = "Digital Marketing Intern | We-Help.in";
    const meta = document.querySelector('meta[name="description"]');
    const desc =
      "Apply for the Digital Marketing Intern role at We-Help.in, a social impact platform by SkillSTEM Academy LLP helping students access free education and skill opportunities across India.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }
    if (typeof window !== "undefined") setPageUrl(window.location.href);

    // JSON-LD JobPosting
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: "Digital Marketing Intern",
      description:
        "Part-time, output-based digital marketing internship supporting We-Help.in — a social initiative by SkillSTEM Academy LLP.",
      employmentType: "INTERN",
      hiringOrganization: {
        "@type": "Organization",
        name: "SkillSTEM Academy LLP",
        sameAs: "https://skillstem.in",
      },
      datePosted: new Date().toISOString().split("T")[0],
      jobLocationType: "TELECOMMUTE",
      applicantLocationRequirements: { "@type": "Country", name: "India" },
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "INR",
        value: { "@type": "QuantitativeValue", value: 5000, unitText: "MONTH" },
      },
    });
    document.head.appendChild(ld);
    return () => {
      document.head.removeChild(ld);
    };
  }, []);

  const handleShare = (platform: "whatsapp" | "linkedin") => {
    const url = encodeURIComponent(pageUrl);
    if (platform === "whatsapp") {
      const text = encodeURIComponent(WHATSAPP_SHARE_MESSAGE);
      window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
      return;
    }
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Link copied — share it with someone who'd be a great fit!");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container py-16 md:py-24">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary mb-5">
              <Sparkles className="h-3.5 w-3.5" /> We're hiring
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              Digital Marketing Intern
            </h1>
            <p className="mt-3 text-lg md:text-xl font-medium text-muted-foreground">
              Part-Time · Impact-Focused Internship · Remote
            </p>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              Join a mission-driven platform helping students across India access free education,
              scholarships, certifications, and skill development opportunities.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="cta" size="lg">
                <a
                  href={APPLY_URL}
                  onClick={() => trackApplyClick("hero")}
                >
                  <Send className="h-4 w-4" /> Apply Now
                </a>
              </Button>
              <Button variant="outline" size="lg" onClick={() => handleShare("whatsapp")}>
                <Share2 className="h-4 w-4" /> Share Opportunity
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Takes less than 3 minutes to apply</p>

            {/* Deadline + scarcity */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground">
                <CalendarClock className="h-4 w-4 text-primary" />
                Applications close on <span className="font-semibold">{APPLICATION_DEADLINE}</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1.5 text-sm font-semibold text-accent-foreground border border-accent/30">
                <Users className="h-4 w-4" />
                Limited positions available — only 2 openings
              </div>
            </div>

            <div className="mt-6">
              <SkillstemAttribution variant="inline" />
            </div>
          </motion.div>
        </div>
      </section>

      <main className="container py-14 md:py-20 space-y-20">
        {/* About We-Help */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">About We-Help.in</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed max-w-3xl">
            <p>
              We-Help.in is a mission-driven platform for students who have completed Class 12, offering
              access to free learning programs, scholarships, certifications, and skill development
              opportunities. All opportunities are curated and shared at zero cost to students.
            </p>
            <p>
              We-Help is a social initiative by <strong className="text-foreground">SkillSTEM Academy LLP</strong>,
              an EdTech organization focused on making quality education accessible to every student in India.
            </p>
          </div>
        </motion.section>

        {/* Why this role matters */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Why This Role Matters</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
            {[
              "Work on a live national platform with real users",
              "Build portfolio-ready digital marketing experience",
              "Gain exposure to strategy, content, campaigns, and analytics",
              "Contribute to a social impact initiative supporting students across India",
            ].map((p) => (
              <div key={p} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{p}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Role objective */}
        <motion.section {...fadeUp} className="rounded-2xl bg-muted/40 p-8 md:p-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Role Objective</h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            To build awareness, drive traffic, and improve student engagement on We-Help.in through practical,
            hands-on digital marketing support.
          </p>
        </motion.section>

        {/* Scope of work */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Scope of Work</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {scopeOfWork.map(({ icon: Icon, title, points }) => (
              <Card key={title} className="hover:shadow-card transition-shadow">
                <CardHeader>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-2">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-lg">{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Learning outcomes */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Learning Outcomes</h2>
          <p className="text-muted-foreground mb-5 max-w-3xl">
            By the end of this internship, the selected candidate should gain hands-on exposure to:
          </p>
          <div className="grid md:grid-cols-2 gap-3 max-w-4xl">
            {[
              "Social media campaign execution",
              "Content writing and content planning",
              "SEO fundamentals",
              "Digital growth strategies",
              "Basic performance reporting",
              "Working on a live EdTech platform",
            ].map((o) => (
              <div key={o} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm text-foreground">{o}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Key deliverables */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Key Deliverables</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {deliverables.map((d) => (
              <div
                key={d}
                className="rounded-xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-5 text-sm font-medium text-foreground"
              >
                {d}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Eligibility */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Eligibility & Competency</h2>
          <div className="grid md:grid-cols-3 gap-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ideal for</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Students or fresh graduates</li>
                  <li>• Candidates interested in digital marketing</li>
                  <li>• Individuals seeking practical, portfolio-building experience</li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic skills</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Good communication</li>
                  <li>• Creativity</li>
                  <li>• Willingness to learn</li>
                  <li>• Basic understanding of social media</li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preferred (not mandatory)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Canva</li>
                  <li>• Basic video editing</li>
                  <li>• Basic SEO awareness</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Time commitment */}
        <motion.section {...fadeUp} className="flex items-start gap-4 max-w-3xl">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Time Commitment</h2>
            <p className="text-muted-foreground">
              Part-time and flexible. This is an output-based internship and not a rigid full-time office role.
            </p>
          </div>
        </motion.section>

        {/* Honorarium & benefits — highlighted */}
        <motion.section {...fadeUp}>
          <div className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 p-8 md:p-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">What you get</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Honorarium & Benefits</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map(({ icon: Icon, label, value }) => (
                <div key={label} className="rounded-xl bg-card border border-border p-5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-3">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className="mt-1 font-semibold text-foreground">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Application Process */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Application Process</h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Interested candidates can apply through the application form. Shortlisted candidates will be
            contacted for the next steps, including a task-based assessment and brief interaction.
          </p>
        </motion.section>

        {/* Apply form */}
        <motion.section {...fadeUp} id="apply" className="scroll-mt-24">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Apply now</h2>
          <p className="text-muted-foreground mb-6 max-w-3xl">
            Fill in this short form. Takes less than 3 minutes. Shortlisted candidates will be contacted by email.
          </p>
          <InternApplicationForm />
        </motion.section>

        {/* Task-based assessment */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Task-Based Assessment</h2>
          <p className="text-sm text-muted-foreground mb-6">Only required if shortlisted.</p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                t: "Task 1",
                d: "Create 1 Instagram post + 1 Reel concept on \"Free Courses After 12th\".",
              },
              {
                t: "Task 2",
                d: "Write 200–300 words on \"Best Free Opportunities for Students in India\".",
              },
              {
                t: "Task 3",
                d: "Share 5–6 points on how you would grow We-Help among students.",
              },
            ].map((task) => (
              <Card key={task.t} className="border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="text-base text-primary">{task.t}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{task.d}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <div className="max-w-3xl">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </motion.section>
      </main>

      {/* Closing banner */}
      <section className="border-t border-border bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground">
        <div className="container py-16 md:py-20 text-center">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto">
            <Heart className="h-10 w-10 mx-auto mb-4 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Learn by doing. Build something meaningful.</h2>
            <p className="text-base md:text-lg opacity-95 leading-relaxed">
              This internship is ideal for individuals who want to learn by doing, build real-world skills,
              and contribute to a meaningful platform that supports students across India.
            </p>
            <p className="mt-3 text-base md:text-lg font-semibold">Join us in making education accessible to all.</p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" variant="secondary">
                <a
                  href={APPLY_URL}
                  onClick={() => trackApplyClick("closing_banner")}
                >
                  <Send className="h-4 w-4" /> Apply Now
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                onClick={() => handleShare("whatsapp")}
              >
                Share on WhatsApp
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                onClick={() => handleShare("linkedin")}
              >
                Share on LinkedIn
              </Button>
            </div>

            <div className="mt-8">
              <Link to="/" className="text-sm underline opacity-90 hover:opacity-100">
                ← Back to We-Help.in
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Sticky mobile Apply CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] shadow-lg">
        <Button asChild variant="cta" size="lg" className="w-full">
          <a
            href={APPLY_URL}
            onClick={() => trackApplyClick("sticky_mobile")}
          >
            <Send className="h-4 w-4" /> Apply Now — closes {APPLICATION_DEADLINE}
          </a>
        </Button>
      </div>
      <div className="md:hidden h-20" aria-hidden="true" />
    </div>
  );
}
